import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AttributeValueToken } from '@sleed/bizcommerce-api';
import { attributeValuesApi } from '~/lib/api/client';
import { uploadAttributeValueImage } from '~/lib/api/attribute-image-upload';
import { formToToken, type AttributeValueFormValues } from '~/lib/schemas/attribute-value';

/** Staged image change for the single attribute-value slot (a `File` uploads; `delete` removes the existing image). */
export interface AttributeValueImagePayload {
	slot1?: File | null;
	delete1?: boolean;
}

interface AttributeValueSavePayload {
	form: AttributeValueFormValues;
	images?: AttributeValueImagePayload;
}

export const attributeValueKeys = {
	all: ['attribute-values'] as const,
	byAttribute: (attributeId: number) => [...attributeValueKeys.all, 'by-attribute', attributeId] as const
};

/**
 * Values for one attribute. Mirrors the legacy AttributeValues modal's `/attributevalues/getbyattribute?id=`
 * (loadMode 'raw') — returns the full set for the attribute in an `AttributeValueTokenList.items` envelope.
 * Disabled when `attributeId` is falsy (e.g. the sheet is closed).
 */
export function useAttributeValuesList(attributeId: number | undefined) {
	return useQuery<AttributeValueToken[]>({
		queryKey: attributeValueKeys.byAttribute(attributeId ?? 0),
		queryFn: async () => (await attributeValuesApi.getByAttribute(attributeId)).data.items ?? [],
		enabled: Boolean(attributeId)
	});
}

/** Create / update / delete an attribute value (parity with the legacy CustomStore insert/update/remove). */
export function useAttributeValueMutations(attributeId: number | undefined) {
	const qc = useQueryClient();
	const invalidate = () => qc.invalidateQueries({ queryKey: attributeValueKeys.byAttribute(attributeId ?? 0) });

	const create = useMutation({
		mutationFn: async ({ form, images }: AttributeValueSavePayload) => {
			const token = { ...formToToken(form), imgdel1: Boolean(images?.delete1) };
			const created = (await attributeValuesApi.insert(token)).data;
			if (images?.slot1 && (created.id ?? form.id)) await uploadAttributeValueImage((created.id ?? form.id)!, '1', images.slot1);
			return created;
		},
		onSuccess: invalidate
	});

	const update = useMutation({
		mutationFn: async ({ form, images }: AttributeValueSavePayload) => {
			const token = { ...formToToken(form), imgdel1: Boolean(images?.delete1) };
			const updated = (await attributeValuesApi.update(token)).data;
			if (images?.slot1 && (updated.id ?? form.id)) await uploadAttributeValueImage((updated.id ?? form.id)!, '1', images.slot1);
			return updated;
		},
		onSuccess: invalidate
	});

	const remove = useMutation({
		mutationFn: async (id: number) => (await attributeValuesApi.remove(id)).data,
		onSuccess: invalidate
	});

	return { create, update, remove };
}
